import { DueState, JobState } from '@/api/generator';
import { DateTime } from 'luxon';
import { DashboardFilterObject, DashboardTimeRangeOption } from './types';
import { defaultDashboardFilter } from './defaults';
import { computed, ref } from 'vue';
import { useServerSideOdataPagination } from '@/utils/useServerSideOdataPagination';
import { useAccountStore } from '@/stores/accountStore';
import { ITEM_ROOT } from 'odata-query';

function createStartRangeFilter(range: { start: DateTime; end: DateTime }) {
  return {
    start: {
      ge: {
        type: 'raw',
        value: range.start.setZone('UTC').toISO({ suppressMilliseconds: true }),
      },
      le: {
        type: 'raw',
        value: range.end.setZone('UTC').toISO({ suppressMilliseconds: true }),
      },
    },
  };
}

function getTimeRangeFilter(filterKey: DashboardTimeRangeOption) {
  const now = DateTime.now();
  const startOfToday = now.startOf('day');
  const endOfToday = now.endOf('day');

  switch (filterKey) {
    case 'today':
      return createStartRangeFilter({
        start: startOfToday,
        end: endOfToday,
      });
    case 'next7days':
      return createStartRangeFilter({
        start: startOfToday,
        end: endOfToday.plus({ days: 7 }),
      });
    case 'next30days':
      return createStartRangeFilter({
        start: startOfToday,
        end: endOfToday.plus({ days: 30 }),
      });
  }
}

export function useDashboardFilter() {
  const filterObject = ref<DashboardFilterObject>(defaultDashboardFilter);

  const { currentUser } = useAccountStore();

  const stateGroup = computed(() => filterObject.value.stateGroup);

  const {
    query: paginationQuery,
    updateQuery: updatePagination,
    onUpdateQuery: onUpdatePagination,
  } = useServerSideOdataPagination();

  const assignmentFilter = computed(() => {
    switch (stateGroup.value) {
      case 'open':
        return {
          or: [
            {
              assignedUserIds: {
                any: {
                  [ITEM_ROOT]: { eq: currentUser?.id },
                },
              },
            },
            {
              assignedRoleIds: {
                any: {
                  [ITEM_ROOT]: { in: currentUser?.assignedRoles ?? [] },
                },
              },
            },
          ],
        };
      case 'review':
        return {
          or: [
            {
              reviewUserIds: {
                any: {
                  [ITEM_ROOT]: { eq: currentUser?.id },
                },
              },
            },
            {
              reviewRoleIds: {
                any: {
                  [ITEM_ROOT]: { in: currentUser?.assignedRoles ?? [] },
                },
              },
            },
          ],
        };
      default:
        return {};
    }
  });

  const targetSystemFilter = computed(() =>
    filterObject.value.targetSystemIds?.length
      ? {
          targetSystemId: { in: filterObject.value.targetSystemIds },
        }
      : {},
  );

  const dueFilter = computed(() =>
    stateGroup.value === 'open'
      ? {
          dueState: { in: [DueState.Overdue, DueState.Due] },
        }
      : {},
  );

  const timeFilter = computed(() =>
    stateGroup.value === 'open' ? getTimeRangeFilter(filterObject.value.timeRange) : {},
  );

  const stateFilter = computed(() => {
    const openStates = [
      JobState.New,
      JobState.Paused,
      JobState.InExecution,
      JobState.ReworkRequested,
    ];

    const reviewStates = [JobState.InReview, JobState.ReviewPending];

    switch (stateGroup.value) {
      case 'open':
        return {
          state: {
            in: openStates,
          },
        };
      case 'review':
        return {
          state: {
            in: reviewStates,
          },
        };
      default:
        return {};
    }
  });

  const dataFilter = computed(() => ({
    ...paginationQuery.value,
    filter: {
      and: [
        assignmentFilter.value,
        stateFilter.value,
        targetSystemFilter.value,
        {
          or: [dueFilter.value, timeFilter.value],
        },
      ],
    },
  }));

  const filterOptionsFilter = computed(() => ({
    filter: {
      and: [assignmentFilter.value, stateFilter.value],
    },
  }));

  return {
    filterObject,
    updatePagination,
    onUpdatePagination,
    filterOptionsFilter,
    dataFilter,
  };
}
